
import React, { useState, useRef, useEffect } from "react";
import { AUTH_STORAGE_KEY, SERVER_URL } from "../../socket";

const PIN_LENGTH = 6;

const DOTS = [
  { id: 0, cx: 60,  cy: 60  }, { id: 1, cx: 150, cy: 60  }, { id: 2, cx: 240, cy: 60  },
  { id: 3, cx: 60,  cy: 150 }, { id: 4, cx: 150, cy: 150 }, { id: 5, cx: 240, cy: 150 },
  { id: 6, cx: 60,  cy: 240 }, { id: 7, cx: 150, cy: 240 }, { id: 8, cx: 240, cy: 240 },
];

function hitDot(x, y, svgRect) {
  const sx = (x - svgRect.left) * (300 / svgRect.width);
  const sy = (y - svgRect.top)  * (300 / svgRect.height);
  return DOTS.find((d) => Math.hypot(d.cx - sx, d.cy - sy) < 30) ?? null;
}

function getAuthHeaders() {
  try {
    const raw    = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.token ? { Authorization: `Bearer ${parsed.token}` } : null;
  } catch { return null; }
}

async function saveLockToDB(payload) {
  const headers = getAuthHeaders();
  if (!headers) throw new Error("Not authenticated");
  const res = await fetch(`${SERVER_URL}/api/settings/app-lock`, {
    method:  "PUT",
    headers: { ...headers, "Content-Type": "application/json" },
    body:    JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to save");
}

function syncToLocal(data) {
  if (data.enabled) {
    localStorage.setItem("app-lock-enabled", "true");
    localStorage.setItem("app-lock-type",    data.type    || "pin");
    localStorage.setItem("app-lock-pin",     data.pin     || "");
    localStorage.setItem("app-lock-pattern", data.pattern || "");
  } else {
    localStorage.removeItem("app-lock-enabled");
    localStorage.removeItem("app-lock-pin");
    localStorage.removeItem("app-lock-pattern");
    localStorage.removeItem("app-lock-type");
  }
}

/* ── Pattern Recorder ── */
function PatternRecorder({ onComplete, onClear, accent = "#22d3ee" }) {
  const svgRef   = useRef(null);
  const [seq, setSeq]       = useState([]);
  const [cursor, setCursor] = useState(null);
  const drawing  = useRef(false);

  const reset = () => { setSeq([]); setCursor(null); drawing.current = false; onClear?.(); };

  const getPos = (e) => e.touches
    ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
    : { x: e.clientX, y: e.clientY };

  const toSVG = (sx, sy) => {
    const rect = svgRef.current.getBoundingClientRect();
    return { x: (sx - rect.left) * (300 / rect.width), y: (sy - rect.top) * (300 / rect.height) };
  };

  const onStart = (e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    const dot = hitDot(x, y, svgRef.current.getBoundingClientRect());
    if (!dot) return;
    drawing.current = true;
    setSeq([dot.id]); setCursor({ x, y });
  };
  const onMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    setCursor({ x, y });
    const dot = hitDot(x, y, svgRef.current.getBoundingClientRect());
    if (dot) setSeq((p) => p.includes(dot.id) ? p : [...p, dot.id]);
  };
  const onEnd = () => {
    if (!drawing.current) return;
    drawing.current = false; setCursor(null);
    if (seq.length >= 4) onComplete?.(seq.join("-"));
    else reset();
  };

  const linePoints = seq.map((id) => `${DOTS[id].cx},${DOTS[id].cy}`).join(" ");
  const cursorSVG  = cursor ? toSVG(cursor.x, cursor.y) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <svg ref={svgRef} viewBox="0 0 300 300"
        style={{ width: 200, height: 200, touchAction: "none", cursor: "crosshair" }}
        onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd}
        onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}>
        {seq.length > 1 && (
          <polyline points={linePoints} fill="none" stroke={accent}
            strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" opacity="0.6" />
        )}
        {cursorSVG && seq.length > 0 && (
          <line x1={DOTS[seq[seq.length-1]].cx} y1={DOTS[seq[seq.length-1]].cy}
            x2={cursorSVG.x} y2={cursorSVG.y} stroke={accent} strokeWidth="2" opacity="0.4" />
        )}
        {DOTS.map((d) => {
          const active = seq.includes(d.id);
          return (
            <g key={d.id}>
              <circle cx={d.cx} cy={d.cy} r={22} fill="rgba(0,0,0,0.03)" />
              <circle cx={d.cx} cy={d.cy} r={active ? 12 : 8}
                fill={active ? accent : "rgba(148,163,184,0.35)"} style={{ transition: "all 0.12s" }} />
              {active && <circle cx={d.cx} cy={d.cy} r={18}
                fill="none" stroke={accent} strokeWidth="1.5" opacity="0.3" />}
            </g>
          );
        })}
      </svg>
      {seq.length > 0 && !drawing.current && (
        <button onClick={reset} style={sty.ghostBtn}>Clear pattern</button>
      )}
      {seq.length > 0 && seq.length < 4 && (
        <p style={{ color: "#ef4444", fontSize: 12, margin: 0 }}>Connect at least 4 dots</p>
      )}
    </div>
  );
}

/* ── PIN Recorder ── */
function PinInput({ label, onComplete, accent = "#1e40af" }) {
  const [pin, setPin]   = useState("");
  const [done, setDone] = useState(false);

  const appendDigit = (d) => {
    if (done || pin.length >= PIN_LENGTH) return;
    const next = pin + d;
    setPin(next);
    if (next.length === PIN_LENGTH) { setDone(true); onComplete?.(next); }
  };
  const backspace = () => { if (!done) setPin((p) => p.slice(0, -1)); };

  const PAD = [["1","2","3"],["4","5","6"],["7","8","9"],["","0","⌫"]];

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
      <p style={{ color: "#64748b", fontSize: 13, margin: 0 }}>{label}</p>
      <div style={{ display: "flex", gap: 10 }}>
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <div key={i} style={{
            width: 12, height: 12, borderRadius: "50%",
            border: `2px solid ${i < pin.length ? accent : "#cbd5e1"}`,
            background: i < pin.length ? accent : "transparent",
            transition: "all 0.15s",
          }} />
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {PAD.map((row, ri) => (
          <div key={ri} style={{ display: "flex", gap: 6 }}>
            {row.map((cell, ci) => {
              if (!cell) return <div key={ci} style={{ width: 60, height: 52 }} />;
              return (
                <button key={cell} style={sty.miniNum}
                  onClick={() => cell === "⌫" ? backspace() : appendDigit(cell)}>
                  {cell === "⌫"
                    ? <i className="fa-solid fa-delete-left" style={{ fontSize: 16, color: "#94a3b8" }} />
                    : <span style={{ fontSize: 18, fontWeight: 400, color: "#1e293b" }}>{cell}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {done && (
        <p style={{ color: "#22c55e", fontSize: 12, margin: 0 }}>
          <i className="fa-solid fa-check" style={{ marginRight: 4 }} />PIN recorded
        </p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   Main AppLockSettings
═══════════════════════════════════════════ */
export default function AppLockSettings() {
  const [enabled,   setEnabled]   = useState(false);
  const [lockType,  setLockType]  = useState("pin");
  const [step,      setStep]      = useState("idle");
  const [pin1,      setPin1]      = useState("");
  const [pattern1,  setPattern1]  = useState("");
  const [msg,       setMsg]       = useState(null);
  const [dbLoading, setDbLoading] = useState(true);

  /* ── Load from DB on mount ── */
  useEffect(() => {
    const load = async () => {
      setDbLoading(true);
      try {
        const headers = getAuthHeaders();
        if (!headers) { setDbLoading(false); return; }
        const res  = await fetch(`${SERVER_URL}/api/settings/app-lock`, { headers });
        const data = await res.json();
        setEnabled(data.enabled === true);
        setLockType(data.type || "pin");
        syncToLocal(data);  // keep localStorage in sync
      } catch {
        // Fallback to localStorage if network fails
        setEnabled(localStorage.getItem("app-lock-enabled") === "true");
        setLockType(localStorage.getItem("app-lock-type") || "pin");
      }
      setDbLoading(false);
    };
    load();
  }, []);

  const showMsg = (type, text, ms = 2500) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), ms);
  };

  /* ── Toggle on/off ── */
  const handleToggle = async (val) => {
    if (val) {
      setStep(lockType === "pin" ? "set-pin" : "set-pattern");
    } else {
      try {
        await saveLockToDB({ enabled: false, type: lockType, pin: "", pattern: "" });
        syncToLocal({ enabled: false });
        setEnabled(false);
        setStep("idle");
        showMsg("success", "App lock disabled on all devices");
      } catch {
        showMsg("error", "Failed to disable lock");
      }
    }
  };

  const handleTypeChange = (t) => { setLockType(t); setStep("idle"); setPin1(""); setPattern1(""); };

  /* ── PIN flow ── */
  const handlePin1Done = (p) => { setPin1(p); setTimeout(() => setStep("confirm-pin"), 400); };
  const handlePin2Done = async (p) => {
    if (p !== pin1) {
      showMsg("error", "PINs don't match. Try again.");
      setPin1(""); setStep("set-pin"); return;
    }
    try {
      const payload = { enabled: true, type: "pin", pin: p, pattern: "" };
      await saveLockToDB(payload);
      syncToLocal(payload);
      setEnabled(true); setStep("done");
      showMsg("success", "PIN lock saved to your account ✓");
      setTimeout(() => setStep("idle"), 1200);
    } catch {
      showMsg("error", "Failed to save. Try again.");
      setStep("set-pin");
    }
  };

  /* ── Pattern flow ── */
  const handlePattern1Done = (p) => { setPattern1(p); setStep("confirm-pattern"); };
  const handlePattern2Done = async (p) => {
    if (p !== pattern1) {
      showMsg("error", "Patterns don't match. Try again.");
      setPattern1(""); setStep("set-pattern"); return;
    }
    try {
      const payload = { enabled: true, type: "pattern", pin: "", pattern: p };
      await saveLockToDB(payload);
      syncToLocal(payload);
      setEnabled(true); setStep("done");
      showMsg("success", "Pattern lock saved to your account ✓");
      setTimeout(() => setStep("idle"), 1200);
    } catch {
      showMsg("error", "Failed to save. Try again.");
      setStep("set-pattern");
    }
  };

  const cancel = () => { setStep("idle"); setPin1(""); setPattern1(""); };

  if (dbLoading) {
    return (
      <div style={{ ...sty.wrap, flexDirection: "row", justifyContent: "center", minHeight: 72 }}>
        <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "#94a3b8", fontSize: 18 }} />
        <span style={{ color: "#94a3b8", fontSize: 13, marginLeft: 8 }}>Loading lock settings…</span>
      </div>
    );
  }

  return (
    <div style={sty.wrap}>
      {/* Header */}
      <div style={sty.header}>
        <div style={sty.iconWrap}>
          <i className="fa-solid fa-lock" style={{ fontSize: 18, color: "#fff" }} />
        </div>
        <div>
          <div style={sty.headerTitle}>App Lock</div>
          <div style={sty.headerSub}>
            {enabled
              ? `Active · ${lockType === "pin" ? "PIN" : "Pattern"} · synced across all devices`
              : "Protect your app with PIN or pattern on every device"}
          </div>
        </div>
        <label className="switch" style={{ marginLeft: "auto", flexShrink: 0 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => handleToggle(e.target.checked)} />
          <span className="slider" />
        </label>
      </div>

      {/* DB sync indicator */}
      {enabled && step === "idle" && (
        <div style={sty.syncBadge}>
          <i className="fa-solid fa-cloud-arrow-up" style={{ marginRight: 6, fontSize: 11 }} />
          Saved to your account — applies on all devices & browsers
        </div>
      )}

      {/* Message */}
      {msg && (
        <div className={`mail-settings-message ${msg.type === "error" ? "error" : "success"}`}
          style={{ marginBottom: 0 }}>{msg.text}</div>
      )}

      {/* Method selector */}
      {enabled && step === "idle" && (
        <div style={sty.typeRow}>
          <span style={sty.typeLabel}>Lock method:</span>
          <div style={sty.typeBtns}>
            {["pin","pattern"].map((t) => (
              <button key={t} style={{ ...sty.typeBtn, ...(lockType === t ? sty.typeBtnActive : {}) }}
                onClick={() => handleTypeChange(t)}>
                <i className={`fa-solid ${t === "pin" ? "fa-hashtag" : "fa-circle-dot"}`}
                  style={{ marginRight: 6 }} />
                {t === "pin" ? "PIN" : "Pattern"}
              </button>
            ))}
          </div>
          <button style={sty.changeBtn}
            onClick={() => setStep(lockType === "pin" ? "set-pin" : "set-pattern")}>
            Change {lockType === "pin" ? "PIN" : "Pattern"}
          </button>
        </div>
      )}

      {!enabled && step === "idle" && (
        <p style={sty.hintTxt}>
          <i className="fa-solid fa-circle-info" style={{ marginRight: 6, color: "#94a3b8" }} />
          Once enabled, the lock screen appears on every device when you open the app.{" "}
          <strong>Ctrl+L</strong> locks anytime.
        </p>
      )}

      {/* Setup flows */}
      {step === "set-pin" && (
        <div style={sty.setupBox}>
          <PinInput key="pin1" label={`Set a ${PIN_LENGTH}-digit PIN`} onComplete={handlePin1Done} />
          <button onClick={cancel} style={sty.cancelBtn}>Cancel</button>
        </div>
      )}
      {step === "confirm-pin" && (
        <div style={sty.setupBox}>
          <PinInput key="pin2" label="Confirm your PIN" onComplete={handlePin2Done} />
          <button onClick={cancel} style={sty.cancelBtn}>Cancel</button>
        </div>
      )}
      {step === "set-pattern" && (
        <div style={sty.setupBox}>
          <p style={{ color: "#64748b", fontSize: 13, margin: 0, textAlign: "center" }}>
            Draw your unlock pattern (min 4 dots)
          </p>
          <PatternRecorder onComplete={handlePattern1Done} onClear={() => {}} />
          <button onClick={cancel} style={sty.cancelBtn}>Cancel</button>
        </div>
      )}
      {step === "confirm-pattern" && (
        <div style={sty.setupBox}>
          <p style={{ color: "#64748b", fontSize: 13, margin: 0, textAlign: "center" }}>
            Draw the same pattern again to confirm
          </p>
          <PatternRecorder key="p2" onComplete={handlePattern2Done} onClear={() => {}} />
          <button onClick={cancel} style={sty.cancelBtn}>Cancel</button>
        </div>
      )}
      {step === "done" && (
        <div style={{ textAlign: "center", padding: "12px 0", color: "#22c55e" }}>
          <i className="fa-solid fa-circle-check" style={{ fontSize: 32 }} />
          <p style={{ margin: "8px 0 0", fontSize: 14 }}>Lock saved to your account</p>
        </div>
      )}
    </div>
  );
}

const sty = {
  wrap: { border: "1px solid #e2e8f0", borderRadius: 14, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14, background: "#fafbff", marginBottom: 8 },
  header: { display: "flex", alignItems: "center", gap: 12 },
  iconWrap: { width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: "linear-gradient(135deg, #1e40af, #6366f1)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(99,102,241,0.3)" },
  headerTitle: { fontWeight: 600, fontSize: 14, color: "#1e293b" },
  headerSub: { fontSize: 12, color: "#64748b", marginTop: 1 },
  syncBadge: { fontSize: 11, color: "#16a34a", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "5px 10px", display: "inline-flex", alignItems: "center" },
  hintTxt: { color: "#94a3b8", fontSize: 13, margin: 0 },
  typeRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 },
  typeLabel: { fontSize: 13, color: "#475569", fontWeight: 500, minWidth: 90 },
  typeBtns: { display: "flex", gap: 6 },
  typeBtn: { border: "1px solid #e2e8f0", borderRadius: 8, padding: "5px 14px", fontSize: 12, fontWeight: 500, cursor: "pointer", background: "#fff", color: "#64748b", transition: "all 0.15s" },
  typeBtnActive: { background: "#eff6ff", border: "1px solid #1e40af", color: "#1e40af" },
  changeBtn: { marginLeft: "auto", border: "1px solid #e2e8f0", borderRadius: 8, padding: "5px 14px", fontSize: 12, cursor: "pointer", background: "#fff", color: "#475569" },
  setupBox: { display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "12px 0", borderTop: "1px solid #f1f5f9" },
  cancelBtn: { border: "1px solid #fecaca", borderRadius: 8, padding: "5px 18px", fontSize: 12, cursor: "pointer", background: "#fff", color: "#ef4444" },
  miniNum: { width: 60, height: 52, borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
  ghostBtn: { background: "transparent", border: "1px solid #e2e8f0", borderRadius: 8, color: "#94a3b8", fontSize: 12, padding: "3px 12px", cursor: "pointer" },
};
